import React, { useState, useRef, useEffect } from "react";

// Modules
import { debounce } from "lodash";

// App
import { client, serverUrl } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { get_csrf_token } from "../../core/auth";
import { postNode } from "../../core/postNode";

// UI components
import MediaLibrary from "../mediaLibrary";
import VoiceRecorder from "../mediaLibrary/voiceRecorder";
import { Container, Row, Col } from "react-bootstrap";
import {
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Slider,
  FormGroup,
  Snackbar,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function AddAudio(props) {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);

  const [audioError, setAudioError] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);

  const [csrf_token, setCSRFToken] = useState(
    props.csrf_token || props.user.csrf_token
  );

  const [volume, setVolume] = useState(
    props.audioData?.volumeValue !== "" &&
      props.audioData?.volumeValue !== null &&
      typeof props.audioData?.volumeValue !== "undefined"
      ? props.audioData?.volumeValue
      : 1.0
  );
  const audioRef = useRef(null);
  // const [audio, setAudio] = useState(props.audioData);

  const handleVolumeChange = debounce((value) => {
    props.onAudioChange({
      ...props?.audioData,
      volumeValue: value,
    });
  }, 500);

  useEffect(() => {
    if (props?.audioData?.customVolume) {
      if (audioRef.current) {
        console.log("volume state", { type: typeof volume, value: volume });
        if (volume !== "" && volume !== null && typeof volume !== "undefined") {
          audioRef.current.volume = volume;
          handleVolumeChange(volume);
        }
      }
    }
    // eslint-disable-next-line
  }, [volume, props?.audioData?.customVolume]);

  const createMedia = (response, media_type, field) => {
    const data = {
      bundle: media_type,
      field: field,
      fid: response.drupal_internal__fid,
      filename: response.filename ? response.filename : `unknown-${Date.now()}`,
    };

    return postNode("/media_library", data, csrf_token);
  };

  const uploadAudioFile = async (
    file,
    {
      mediaType = "audio",
      field = "field_media_audio_file",
      onProgress = () => {},
    } = {}
  ) => {
    const url = `${serverUrl}/jsonapi/media/${mediaType}/${field}`;
    let request;

    async function performUpload(csrfToken) {
      return new Promise((resolve, reject) => {
        request = new XMLHttpRequest();
        request.open("POST", url);
        request.withCredentials = true;

        request.setRequestHeader("Accept", "application/vnd.api+json");
        request.setRequestHeader("Content-Type", "application/octet-stream");
        request.setRequestHeader(
          "Content-disposition",
          `file; filename="${encodeURI(file.name)}"`
        );
        request.setRequestHeader("X-CSRF-Token", csrfToken);

        // Progress
        request.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress({
              loaded: e.loaded,
              total: e.total,
              percent: (e.loaded / e.total) * 100,
            });
          }
        };

        request.onerror = () => reject("Network error during upload");

        request.onload = async () => {
          // SUCCESS CASE
          if (request.status >= 200 && request.status < 300) {
            try {
              const response = JSON.parse(request.responseText);

              // Create media entity (same as FilePond flow)
              const result = await createMedia(
                response.data.attributes,
                mediaType,
                field
              );

              resolve(result);
            } catch (err) {
              reject("Failed to parse or create media");
            }
            return;
          }

          // TOKEN EXPIRED → REFRESH & RETRY
          if (
            request.status === 403 &&
            request.responseText.includes(
              "X-CSRF-Token request header is invalid"
            )
          ) {
            try {
              const newTokenRes = await get_csrf_token();

              if (newTokenRes.data) {
                setCSRFToken(newTokenRes.data);
                const retry = await performUpload(newTokenRes.data);
                resolve(retry);
              } else {
                reject("Failed to refresh CSRF token");
              }
            } catch (err) {
              reject("CSRF refresh error");
            }
            return;
          }

          // OTHER ERRORS
          reject(`Upload failed with status ${request.status}`);
        };

        request.send(file);
      });
    }

    return performUpload(csrf_token);
  };

  const uploadAudioRecording = async (file) => {
    try {
      const mediaUpload = await uploadAudioFile(file);

      if (mediaUpload?.data?.mid[0]?.value) {
        let path = `media_api?mid=${mediaUpload?.data?.mid[0]?.value}`;

        getOnlineNode(path)
          .then((response) => {
            const media = response.data.rows[0];
            props.onAudioChange({
              ...media,
              src: media.field_media_audio,
              autoPlay: false,
              loop: false,
            });

            setAudioUploading(false);
            setShowVoiceRecorder(false);
          })
          .catch((_error) => {
            setAudioUploading(false);
            setAudioError("Failed to attach audio. Please try again.");
          });
      } else {
        setAudioUploading(false);
        setAudioError("Failed to upload audio. Please try again.");
      }
    } catch (err) {
      setAudioUploading(false);
      setAudioError("Failed to upload audio. Please try again.");
    }
  };

  if (showVoiceRecorder) {
    return (
      <>
        <VoiceRecorder
          format="audio/mpeg"
          audioSaving={audioUploading}
          onSave={(blob) => {
            setAudioUploading(true);

            const recordingName = `${
              props.user.current_user.uid
            }-recording-${Date.now()}`;
            const file = new File([blob], `${recordingName}.mp3`, {
              type: "audio/mpeg",
            });

            uploadAudioRecording(file);
          }}
        />
        <Snackbar
          open={audioError ? true : false}
          autoHideDuration={3000}
          onClose={() => {
            setAudioError(null);
          }}
          message={audioError}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => {
                setAudioError(null);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </>
    );
  }

  return (
    <>
      <MediaLibrary
        user={props.user}
        mediaType="audio"
        setShowMediaLibrary={(condition) => {
          setShowMediaLibrary(condition);
        }}
        showMediaLibrary={showMediaLibrary}
        onMediaInsert={(media) => {
          props.onAudioChange({
            ...media,
            autoPlay: false,
            loop: false,
          });
        }}
        selectedMid={props.audioData?.mid}
      />

      {props.audioData ? (
        <Container
          className={`${
            props.presentationSetting
              ? "adv-presentation-settings-sub-container"
              : "adv-slide-settings-sub-container"
          }  ${props?.audioData.src ? "audio" : ""} px-0`}
        >
          <Row className="mb-4">
            <Col xs={6} className="d-flex flex-column pe-4">
              <Row>
                <Col xs={12}>
                  <audio
                    ref={audioRef}
                    controls
                    src={props?.audioData.src}
                    className="w-100"
                  />
                </Col>
              </Row>
              <Row className="add-audio-row">
                <Col xs={12}>
                  <FormGroup className="form-group mb-0">
                    <FormControlLabel
                      label="Autoplay"
                      control={
                        <Checkbox
                          size="small"
                          checked={props?.audioData.autoPlay}
                          onChange={(event) => {
                            props.onAudioChange({
                              ...props?.audioData,
                              autoPlay: event.target.checked,
                            });
                          }}
                        />
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row className="add-audio-row">
                <FormGroup className="form-group mb-0">
                  <FormControlLabel
                    label="Loop audio"
                    control={
                      <Checkbox
                        size="small"
                        checked={
                          props?.audioData?.loop
                            ? props?.audioData?.loop
                            : false
                        }
                        onChange={(event) => {
                          props.onAudioChange({
                            ...props?.audioData,
                            loop: event.target.checked,
                          });
                        }}
                      />
                    }
                  />
                </FormGroup>
              </Row>
              <Row className="d-flex align-items-center add-audio-row">
                <Col xs={"auto"}>
                  <FormGroup className="form-group mb-0">
                    <FormControlLabel
                      label="Volume"
                      control={
                        <Checkbox
                          size="small"
                          checked={
                            props?.audioData?.customVolume
                              ? props?.audioData?.customVolume
                              : false
                          }
                          onChange={(event) => {
                            props.onAudioChange({
                              ...props?.audioData,
                              customVolume: event.target.checked,
                            });
                          }}
                        />
                      }
                    />
                  </FormGroup>
                </Col>
                <Col className="d-flex align-items-center pe-3">
                  <Slider
                    disabled={!props?.audioData?.customVolume}
                    marks
                    step={0.1}
                    defaultValue={1.0}
                    min={0}
                    max={1.0}
                    value={volume}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => <div>{value * 100}%</div>}
                    onChange={(e, value) => {
                      setVolume(value);
                    }}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={6} className="d-flex flex-column ps-4">
              <Row className="add-audio-row">
                <Col xs={3} className="pe-0">
                  <Typography variant="form-group-label" owner="admin">
                    File Name:
                  </Typography>
                </Col>
                <Col>
                  <Typography
                    variant="form-group-label"
                    owner="admin"
                    color="adminText.dark"
                    weight="light"
                  >
                    {props?.audioData?.name}
                  </Typography>
                </Col>
              </Row>
              <Row className="add-audio-row">
                <Col xs={3} className="pe-0">
                  <Typography variant="form-group-label" owner="admin">
                    MID:
                  </Typography>
                </Col>
                <Col>
                  <Typography
                    variant="form-group-label"
                    owner="admin"
                    color="adminText.dark"
                    weight="light"
                  >
                    {props?.audioData?.mid}
                  </Typography>
                </Col>
              </Row>
              <Row className="add-audio-row">
                <Col xs={3} className="pe-0">
                  <Typography variant="form-group-label" owner="admin">
                    File size:
                  </Typography>
                </Col>
                <Col>
                  <Typography
                    variant="form-group-label"
                    owner="admin"
                    color="adminText.dark"
                    weight="light"
                  >
                    45mb
                  </Typography>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="btn-grp justify-content-between">
            <Col xs={"auto"}>
              <Button
                className="change-btn"
                variant="contained"
                color="primary"
                onClick={() => {
                  setShowMediaLibrary(true);
                }}
              >
                Change audio
              </Button>
            </Col>
            <Col xs={"auto"}>
              <Button
                className="remove-btn"
                variant="contained"
                color="error"
                onClick={() => {
                  props.onAudioChange(null);
                }}
              >
                Remove audio
              </Button>
            </Col>
          </Row>
        </Container>
      ) : (
        <Container className="adv-slide-settings-sub-container audio-modal">
          <Row className="d-flex justify-content-center">
            <Col xs={6}>
              <Row className="flex-column text-center" style={{ gap: "50px" }}>
                <Col className="audio-hint">
                  <Typography variant="body2" owner="admin">
                    To attach a sound to the{" "}
                    {props.slideSetting ? "slide" : "presentation"}, please
                    select or upload an audio file via the media library.
                  </Typography>
                </Col>
                <Col>
                  <Button
                    className="media-lib-btn"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setShowMediaLibrary(true);
                      // setSlideAudio(true)
                    }}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      {client === "radisson"
                        ? "Media library"
                        : "Asset Library"}
                    </Typography>
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col xs={6}>
              <Row className="flex-column text-center" style={{ gap: "50px" }}>
                <Col className="audio-hint">
                  <Typography variant="body2" owner="admin">
                    To record a new voiceover, use the recording feature via the
                    button below.
                  </Typography>
                </Col>
                <Col>
                  <Button
                    className="media-lib-btn"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setShowVoiceRecorder(true);
                    }}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      Record voiceover
                    </Typography>
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
}
